import { useEffect, useMemo, useState } from "react";
import { Radio, RadioGroup } from "V2.0/common/radio";
import { Button } from "V2.0/common/button";
import { Badge } from "V2.0/common/badge";
import { PencilIcon } from "@heroicons/react/24/outline";
import TagsSection from "../tags/TagsSection";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "V2.0/common/dialog";

enum TagFilterMode {
  Untagged = "Untagged",
  AssignedTags = "AssignedTags",
}

function TagsSelect(props) {
  const { selectedTags, availableTags, onClose, onUpdateSelect } = props;
  const [tagsSelected, setTagsSelected] = useState(selectedTags);

  const handleAdd = (tag) => {
    setTagsSelected((prevTags) => {
      return [...prevTags, tag];
    });
  };

  const handleRemove = (tag) => {
    setTagsSelected((prevTags) => prevTags.filter((_tag) => tag !== _tag));
  };

  const handleDone = () => {
    onUpdateSelect(tagsSelected);
    onClose();
  };

  const tags = useMemo(() => {
    return availableTags.map((tag) => {
      return { name: tag, isChecked: tagsSelected.includes(tag) };
    });
  }, [tagsSelected, availableTags]);

  return (
    <Dialog
      open={true}
      onClose={onClose}
    >
      <DialogTitle>Tags</DialogTitle>
      <DialogBody>
        {Boolean(tags.length) ? (
          <TagsSection
            tags={tags}
            onCheckTag={handleAdd}
            onUncheckTag={handleRemove}
          />
        ) : (
          <div className="w-full h-40 border rounded-lg flex items-center justify-center">
            <p className="text-sm text-gray-600">No tags available</p>
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <Button
          outline
          onClick={handleDone}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AssignedTags(props) {
  const { assignedTags, tags, updateAssignedTags, disabled } = props;

  const [openTagSelect, setOpenTagSelect] = useState(false);

  return (
    <>
      <div className="flex flex-col gap-y-2 border rounded-lg p-4">
        <div className="flex justify-between">
          <p className="text-xs text-gray-400">Selected Tags</p>
          <Button
            disabled={disabled}
            plain
            onClick={() => setOpenTagSelect(true)}
          >
            <PencilIcon />
          </Button>
        </div>
        <div>
          {Boolean(assignedTags.length) ? (
            <div className="flex flex-row flex-wrap gap-x-2 gap-y-4">
              {assignedTags.map((tag, idx) => {
                return (
                  <Badge
                    color="blue"
                    key={idx}
                  >
                    {tag}
                  </Badge>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-400">No tags assigned</p>
          )}
        </div>
      </div>
      {openTagSelect && (
        <TagsSelect
          selectedTags={assignedTags}
          availableTags={tags}
          onClose={() => setOpenTagSelect(false)}
          onUpdateSelect={(tags) => updateAssignedTags(tags)}
        />
      )}
    </>
  );
}

export default function TagsFilters(props) {
  const { tags, untagged, assignedTags, updateUntagged, updateAssignedTags } =
    props;

  const [tagFilterMode, setTagFilterMode] = useState(null);

  useEffect(() => {
    if (untagged) {
      setTagFilterMode(TagFilterMode.Untagged);
      return;
    }

    if (assignedTags.length > 0) {
      setTagFilterMode(TagFilterMode.AssignedTags);
      return;
    }

    setTagFilterMode(null);
  }, [untagged, assignedTags]);

  return (
    <div>
      <RadioGroup value={tagFilterMode}>
        <div className="flex items-center gap-x-2">
          <Radio
            value={null}
            onClick={(e) => {
              setTagFilterMode(null);
              updateUntagged(false);
              updateAssignedTags([]);
            }}
          />
          <p className="text-sm">None</p>
        </div>
        <div className="flex items-center gap-x-2">
          <Radio
            value={TagFilterMode.Untagged}
            onClick={(e) => {
              setTagFilterMode(TagFilterMode.Untagged);
              updateUntagged(true);
              updateAssignedTags([]);
            }}
          />
          <p className="text-sm">Untagged Conversations</p>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-x-2">
            <Radio
              value={TagFilterMode.AssignedTags}
              onClick={(e) => {
                setTagFilterMode(TagFilterMode.AssignedTags);
                updateUntagged(false);
              }}
            />
            <p className="text-sm">Contain the following tags</p>
          </div>
          <div className="ml-4">
            <AssignedTags
              tags={tags}
              assignedTags={assignedTags}
              updateAssignedTags={updateAssignedTags}
              disabled={tagFilterMode !== TagFilterMode.AssignedTags}
            />
          </div>
        </div>
      </RadioGroup>
    </div>
  );
}
